import { isSecuredVisible } from '@components/security/securedRules';
import type { Clause } from '@components/security/types';
import type { SecuredConfig } from '@components/security/securedRules';
import type { MassActionConfig } from './massActionTypes';

export const isMassActionVisible = <T,>(item: MassActionConfig<T>, selected: T[]): boolean =>
  (!item.visible || item.visible(selected)) && isSecuredVisible(item.secured?.(selected));

/**
 * Whether the table should mount its selection controls at all. In security mode the answer is yes
 * as soon as *any* mass action is configured: hidden actions still have to be inspectable, and
 * without the checkboxes there is no selection to evaluate their rules against.
 */
export const hasInspectableMassAction = <T,>(items: MassActionConfig<T>[], selected: T[], securityMode: boolean): boolean =>
  securityMode ? items.length > 0 : items.some(item => isMassActionVisible(item, selected));

/**
 * Which mass actions the buttons row renders.
 *
 * Security mode filters nothing out: an action hidden by its own `visible()` predicate still has to
 * be inspectable, and it cannot explain itself if it never renders. Actions hidden by `secured` need
 * no handling here — RingilButton's decoration turns them into a box one level down.
 */
export const shownMassActions = <T,>(items: MassActionConfig<T>[], selected: T[], securityMode: boolean): MassActionConfig<T>[] =>
  securityMode ? items : items.filter(item => !item.visible || item.visible(selected));

interface SecuredForOptions<T> {
  idx: number;
  idPrefix: string;
  selected: T[];
  securityMode: boolean;
  disabled: boolean;
}

/**
 * The config RingilButton decorates a mass action with.
 *
 * Outside security mode this is the action's own config, untouched. Inside, two things are added: a
 * bare `visible()` predicate is surfaced as a synthesized clause — it is a real visibility decision
 * and would otherwise be invisible to the overlay — and the disabled state rides along as a note,
 * since "no permission" and "wrong selection" are both answers to why the button cannot be pressed.
 */
export const massActionSecuredConfig = <T,>(item: MassActionConfig<T>, opts: SecuredForOptions<T>): SecuredConfig | undefined => {
  const { idx, idPrefix, selected, securityMode, disabled } = opts;
  const own = item.secured?.(selected);
  if (!securityMode) return own;

  const predicateClause: Clause[] = item.visible ? [{ type: 'custom', label: 'visible() predicate', satisfied: item.visible(selected) }] : [];
  if (!own && predicateClause.length === 0) return undefined;

  const ownVisible = own?.visible;
  const ownVisibleItems = ownVisible == null ? [] : Array.isArray(ownVisible) ? ownVisible : [ownVisible];
  const reason = disabled ? item.disabledReason?.(selected) : undefined;

  return {
    ...own,
    id: own?.id ?? `${idPrefix}-massAction-${idx}`,
    label: own?.label ?? item.label,
    visible: [...ownVisibleItems, ...predicateClause],
    note: disabled ? (reason ? `disabled for this selection: ${reason}` : 'disabled for this selection') : undefined,
  };
};
